import { HttpClient, HttpHeaders, HttpParams, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { API, Auth } from 'aws-amplify';
import { API_ENDPOINTS, INITIAL_CONSTANTS } from './constants/api-endpoints';
import { environment } from '../../../environments/environment';
import { BehaviorSubject, catchError, firstValueFrom, Observable, of } from 'rxjs';
import {
    Bank,
    CustomerMeResponse,
    PayCategory,
    PayProviders,
    Payee,
    ProviderList,
    ProspectMeResponse,
    ValidateReferenceNumberPayload,
    GetPayCategoriesParams,
    GetPayProvidersParams,
    GetPayProvidersListParams,
    TransactionServiceListResponse,
    Group
} from '../models/interface';

type UnifiedCustomerData = CustomerMeResponse | ProspectMeResponse;

@Injectable({
    providedIn: 'root'
})
export class ApiService {
    private userData = new BehaviorSubject<UnifiedCustomerData | null>(null);
    userData$ = this.userData.asObservable();

    private locationPermissionSubject = new BehaviorSubject<boolean>(true);
    locationPermission$ = this.locationPermissionSubject.asObservable();

    private userGroup = new BehaviorSubject<Group | null>(null);
    group$: Observable<Group | null> = this.userGroup.asObservable();

    // BehaviorSubject to hold user documents
    private userDocuments = new BehaviorSubject<any[] | null>(null);
    userDocuments$: Observable<any[] | null> = this.userDocuments.asObservable();

    // BehaviorSubject to hold customer profile data
    private userProfile = new BehaviorSubject<CustomerMeResponse | null>(null);
    userProfile$: Observable<CustomerMeResponse | null> = this.userProfile.asObservable();

    private payeesList = new BehaviorSubject<any[] | null>(null);
    payees$: Observable<any[] | null> = this.payeesList.asObservable();

    private balanceList = new BehaviorSubject<any[] | null>(null);
    balanceList$: Observable<any[] | null> = this.balanceList.asObservable();

    private bankList = new BehaviorSubject<any[] | null>(null);
    bankList$: Observable<any[] | null> = this.bankList.asObservable();

    private categoriesList = new BehaviorSubject<PayCategory[] | null>(null);
    categories$: Observable<PayCategory[] | null> = this.categoriesList.asObservable();

    private providersList = new BehaviorSubject<PayProviders[] | null>(null);
    providers$: Observable<PayProviders[] | null> = this.providersList.asObservable();

    private customerBulkList = new BehaviorSubject<any[] | null>(null);
    customerBulkList$: Observable<any[] | null> = this.customerBulkList.asObservable();

    private customerBulkTransactionsList = new BehaviorSubject<any[] | null>(null);
    customerBulkTransactionsList$: Observable<any[] | null> = this.customerBulkTransactionsList.asObservable();

    private customerBulkTransactionsPending = new BehaviorSubject<any[] | null>(null);
    customerBulkTransactionsPending$: Observable<any[] | null> = this.customerBulkTransactionsPending.asObservable();
    private transactionTracking = new BehaviorSubject<any | null>(null);
    transactionTracking$: Observable<any | null> = this.transactionTracking.asObservable();

    private agreementsOfContractAndTerms = new BehaviorSubject<any | null>(null);
    agreementsOfContractAndTerms$: Observable<any | null> = this.agreementsOfContractAndTerms.asObservable();

    private customerTransactionalityFind = new BehaviorSubject<any[] | null>(null);
    customerTransactionalityFind$: Observable<any[] | null> = this.customerTransactionalityFind.asObservable();

    //BehaviorSubject to hold user transactionServiceList
    private transactionServiceList = new BehaviorSubject<TransactionServiceListResponse | null>(null);
    transactionServiceList$: Observable<TransactionServiceListResponse | null> = this.transactionServiceList.asObservable();

    constructor(
        private router: Router,
        public http: HttpClient
    ) {}

    /**
     * Saca al usuario a login SOLO si su sesión de Cognito realmente ya no existe.
     *
     * Antes cualquier 403 de cualquier endpoint disparaba `router.navigate('/landing/login')` a secas.
     * El problema: un 403 de un endpoint de negocio casi nunca significa "se cayó la sesión" — suele
     * ser "tu rol no puede ver este recurso" o un error del propio endpoint. Y como la sesión de
     * Cognito seguía viva, `PublicGuard` (que protege /landing/login para no-autenticados) rebotaba
     * al usuario a `/` → el dashboard. Resultado: si fallaba una sola petición al entrar a una
     * pantalla, la pantalla desaparecía y el toast de error se perdía en la navegación, sin ninguna
     * pista de qué pasó.
     *
     * Ahora se comprueba la sesión antes de decidir: si sigue viva, el error simplemente se propaga
     * al componente (que ya lo muestra con su MessageService) y el usuario se queda donde está.
     */
    private async redirectIfSessionExpired(status: number | undefined): Promise<void> {
        if (status !== HttpStatusCode.Unauthorized && status !== HttpStatusCode.Forbidden) return;

        try {
            await Auth.currentAuthenticatedUser();
            // Sesión válida ⇒ es un problema de permisos o de negocio, no de autenticación.
        } catch {
            this.router.navigate([INITIAL_CONSTANTS.TITLE]);
        }
    }

    public async apiCall(method: 'get' | 'post' | 'put' | 'del', apiName: string, path: string, body?: any, options: any = {}): Promise<any> {
        try {
            if (options && typeof options === 'object' && options.headers === undefined) {
                options = { headers: options };
            }

            if (options.headers && typeof options.headers === 'object') {
                Object.keys(options.headers).forEach((key) => {
                    if (typeof options.headers[key] !== 'string') {
                        console.warn(`Non-string header value for ${key}:`, options.headers[key]);
                        options.headers[key] = String(options.headers[key] || '');
                    }
                });
            }

            const myInit = { ...options, ...(body ? { body } : {}) };
            const result = await API[method](apiName, path, myInit);
            return result;
        } catch (error: any) {
            console.error(`Error in ${method.toUpperCase()} ${path}:`, error);
            const errorResponse = error?.response?.data || {};
            const errorMessage = errorResponse?.message || error?.message;
            const status = error?.response?.status || error?.response?.data?.statusCode;

            await this.redirectIfSessionExpired(status);

            throw {
                success: false,
                statusCode: errorResponse?.statusCode || error?.response?.status || 500,
                message: errorMessage
            };
        }
    }

    getCognitoAccessToken = async () => {
        const readSessionToken = (session: any) => {
            if (!session) return null;

            const idToken = session?.getAccessToken?.()?.getJwtToken?.();
            return idToken || null;
        };

        try {
            const session = await Auth.currentSession();
            const directToken = readSessionToken(session);
            if (directToken) return directToken;
        } catch {
            // Ignore and try authenticated-user fallback.
        }

        try {
            const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
            const userSession = user?.signInUserSession || user?.getSignInUserSession?.();
            const userToken = readSessionToken(userSession);
            if (userToken) return userToken;
        } catch {}

        return null;
    };

    getCognitoIdToken = async () => {
        const readSessionToken = (session: any) => {
            if (!session) return null;

            const idToken = session?.getIdToken?.()?.getJwtToken?.();
            return idToken || null;
        };

        try {
            const session = await Auth.currentSession();
            const directToken = readSessionToken(session);
            if (directToken) return directToken;
        } catch {
            // Ignore and try authenticated-user fallback.
        }

        try {
            const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
            const userSession = user?.signInUserSession || user?.getSignInUserSession?.();
            const userToken = readSessionToken(userSession);
            if (userToken) return userToken;
        } catch {}

        return null;
    };

    /**
     * Email del admin logueado (atributo `email` de Cognito). Lo necesita el reporte de
     * transacciones del detalle de cliente (/reports/report/send), que es asíncrono y manda
     * el archivo por correo — el destinatario es quien lo pidió, no el cliente del reporte.
     */
    getCognitoUserEmail = async (): Promise<string | null> => {
        try {
            const user = await Auth.currentAuthenticatedUser();
            return user?.attributes?.email || null;
        } catch {
            return null;
        }
    };

    public async coreApiCall(
        method: 'get' | 'post' | 'put' | 'delete',
        path: string,
        body?: any,
        params?: any,
        apiKeyName: string = 'x-api-key',
        apikey: string = environment.eplata_api_key_auth,
        // 'blob'/'text' habilitan descargas binarias (PDFs, etc.) contra administrador-backend
        // (ver /admin-back/api/* en api-endpoints.ts), que responde streams en vez de JSON.
        responseType: 'json' | 'blob' | 'text' = 'json',
        // Por defecto se manda el accessToken (comportamiento histórico). Algunos endpoints
        // (ej. OPERATIONS.TOKEN_GENERATE/TOKEN_VERIFY en bservices) requieren idToken en su
        // lugar — pasar `true` para ese caso puntual, en vez de crear otro método aparte.
        useIdToken: boolean = false,
        // Si es `true`, un 403 no redirige al dashboard: el error se propaga al llamador
        // para que lo maneje con su propio toast en pantalla.
        suppressRedirectOn403: boolean = false
    ): Promise<any> {
        // Si path es una URL completa (empieza con http) se usa tal cual; si no, se antepone API_EPLATA_URL.
        const url = path.startsWith('http') ? path : `${environment.API_EPLATA_URL}${path}`;

        // FormData (subida de archivos) necesita que el navegador ponga su propio
        // Content-Type con boundary; si lo forzamos a application/json, el backend no puede
        // parsear el multipart.
        const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;

        const token = useIdToken ? await this.getCognitoIdToken() : await this.getCognitoAccessToken();
        const headers = new HttpHeaders({
            ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
            Authorization: `Bearer ${token || ''}`,
            [apiKeyName]: apikey || ''
        });

        try {
            const request$ = this.http.request(method, url, {
                body,
                headers,
                params: new HttpParams({ fromObject: params || {} }),
                responseType
            });

            return await firstValueFrom(request$);
        } catch (error: any) {
            if (!suppressRedirectOn403) {
                await this.redirectIfSessionExpired(error?.status);
            }

            throw {
                success: false,
                statusCode: error.status || 500,
                message: error.error?.message || error.message || 'Server Error'
            };
        }
    }

    // get(apiName: string, path: string): Promise<any> {
    //   return this.apiCall("get", apiName, path);
    // }

    get(apiName: string, path: string, options: any = {}): Promise<any> {
        // Pass the options object to the underlying apiCall
        return this.apiCall('get', apiName, path, undefined, options);
    }

    post(apiName: string, path: string, body: any): Promise<any> {
        return this.apiCall('post', apiName, path, body);
    }

    put(apiName: string, path: string, body: any): Promise<any> {
        return this.apiCall('put', apiName, path, body);
    }

    del(apiName: string, path: string, body?: any): Promise<any> {
        return this.apiCall('del', apiName, path, body);
    }

    async fetchGroup(username: string): Promise<Group | null> {
        try {
            const groupUrl = this.fetchGroups(username);
            const response = await firstValueFrom(
                this.http.get<Group>(groupUrl).pipe(
                    catchError((error) => {
                        console.error('Error fetching group:', error);
                        return of(null);
                    })
                )
            );
            this.userGroup.next(response);
            return response;
        } catch (error) {
            this.userGroup.next(null);
            console.error('Error in fetchGroups:', error);
            return null;
        }
    }

    fetchGroups(id: string): string {
        if (!id) {
            throw new Error('ID is required for fetchGroups');
        }
        const url = `${environment.API_GROUP_URL}${API_ENDPOINTS.PATHS.GROUPS}${id}`;
        return url;
    }

    // Retrieves customer contracts filtered by document type
    fetchContracts(docType: string): Promise<any> {
        if (!docType) {
            throw new Error('docType is required for fetchContracts');
        }
        return this.apiCall('get', API_ENDPOINTS.EPLATA_CUSTOMER_API, `${API_ENDPOINTS.PATHS.CONTRACTS}${docType}`);
    }

    // Updates the session for a prospect user by sending the session ID
    updateProspectSession(sessionId: string): Promise<any> {
        if (!sessionId) {
            throw new Error('sessionId is required for updateSession');
        }
        return this.apiCall('put', API_ENDPOINTS.EPLATA_PROSPECT_API, API_ENDPOINTS.PATHS.UPDATED_PROSPECT_SESSION, {
            sessionId
        });
    }

    // Updates the session for a customer user by sending the session ID
    updateCustomerSession(sessionId: string): Promise<any> {
        if (!sessionId) {
            throw new Error('sessionId is required for updateSession');
        }
        return this.apiCall('put', API_ENDPOINTS.EPLATA_CUSTOMER_API, API_ENDPOINTS.PATHS.UPDATED_CUSTOMER_SESSION, {
            sessionId
        });
    }

    // Records a failed login attempt for a user, used in AuthService
    attemptsFailureLogin(body: any): Promise<any> {
        return this.apiCall('put', API_ENDPOINTS.CARD_USER_API_NAME, API_ENDPOINTS.PATHS.ATTEMPTS, body);
    }

    // Retrieves the current customer’s profile information
    // customerMe(): Promise<CustomerMeResponse> {
    //   const responsePromise = this.apiCall(
    //     "get",
    //     API_ENDPOINTS.EPLATA_CUSTOMER_API,
    //     API_ENDPOINTS.PATHS.CUSTOMER_ME
    //   );
    //   responsePromise
    //     .then((response: CustomerMeResponse) => {
    //       this.userData.next(response);
    //     })
    //     .catch((error) => {
    //       console.error("customerMe error:", error);
    //       this.userData.next(null);
    //       throw error;
    //     });
    //   return responsePromise;
    // }

    customerMe(): Promise<CustomerMeResponse> {
        const responsePromise = this.apiCall('get', API_ENDPOINTS.EPLATA_CUSTOMER_API, API_ENDPOINTS.PATHS.CUSTOMER_ME);
        responsePromise
            .then((response: CustomerMeResponse) => {
                this.userData.next(response);
            })
            .catch((error) => {
                console.error('customerMe error:', error);
                this.userData.next(null);
                throw error;
            });
        return responsePromise;
    }

    // Retrieves the current prospect’s profile information
    prospectMe(): Promise<ProspectMeResponse> {
        const responsePromise = this.apiCall('get', API_ENDPOINTS.EPLATA_PROSPECT_API, API_ENDPOINTS.PATHS.PROSPECT_ME);
        responsePromise
            .then((response: ProspectMeResponse) => {
                this.userData.next(response);
            })
            .catch((error) => {
                console.error('prospetMe error:', error);
                this.userData.next(null);
                throw error;
            });
        return responsePromise;
    }

    //KYC Api
    kycApi(payload: any, accountTypes: string): Promise<any | undefined> {
        try {
            if (accountTypes === 'prospect') {
                const response = this.apiCall('post', API_ENDPOINTS.EPLATA_PROSPECT_API, API_ENDPOINTS.PATHS.KYCPROSPECT, payload);
                return response;
            } else {
                const response = this.apiCall('post', API_ENDPOINTS.EPLATA_CUSTOMER_API, API_ENDPOINTS.PATHS.KYCCUSTOMER, payload);
                return response;
            }
        } catch (error) {
            console.error('Error in kycApi:', error);
            return Promise.resolve(undefined);
        }
    }

    // Retrieves the device's current geolocation coordinates
    getCurrentLocation(): Promise<GeolocationPosition> {
        return new Promise((resolve, reject) => {
            if (!navigator.geolocation) {
                this.locationPermissionSubject.next(false);
                reject(new Error('Geolocation is not supported by this browser.'));
                return;
            }
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    this.locationPermissionSubject.next(false);
                    resolve(position);
                },
                (error) => {
                    console.warn('Geolocation error:', error);
                    this.locationPermissionSubject.next(true);
                    reject(error);
                }
            );
        });
    }

    // Uploads a file metadata for a prospect to the file register endpoint
    uploadProspectFile(payload: { key: string; type: string; name: string }): Promise<any> {
        return this.apiCall('post', API_ENDPOINTS.EPLATA_PROSPECT_API, API_ENDPOINTS.PATHS.PROSPECT_DOCUMENTS_UPLOAD, payload);
    }

    // Uploads a file metadata for a customer to the file register endpoint
    uploadCustomerFile(payload: { key: string; type: string; name: string }): Promise<any> {
        return this.apiCall('post', API_ENDPOINTS.EPLATA_CUSTOMER_API, API_ENDPOINTS.PATHS.CUSTOMER_DOCUMENTS_UPLOAD, payload);
    }

    // Retrieves the list of documents for the current prospect
    async fetchProspectDocuments(): Promise<any> {
        const responsePromise = this.apiCall('get', API_ENDPOINTS.EPLATA_PROSPECT_API, API_ENDPOINTS.PATHS.PROSPECT_GET_DOCUMENTS);
        responsePromise
            .then((response: any[]) => {
                this.userDocuments.next(response);
            })
            .catch((error) => {
                console.error('fetchProspectDocuments error:', error);
                this.userDocuments.next(null);
                throw error;
            });
        return responsePromise;
    }

    // Retrieves the list of documents for the current customer
    async fetchCustomerDocuments(): Promise<any> {
        const responsePromise = this.apiCall('get', API_ENDPOINTS.EPLATA_CUSTOMER_API, API_ENDPOINTS.PATHS.CUSTOMER_GET_DOCUMENTS);
        responsePromise
            .then((response: any[]) => {
                this.userDocuments.next(response);
            })
            .catch((error) => {
                console.error('fetchCustomerDocuments error:', error);
                this.userDocuments.next(null);
                throw error;
            });
        return responsePromise;
    }

    // Retrieves the customer profile information
    async customerProfile(): Promise<CustomerMeResponse> {
        const responsePromise = this.apiCall('get', API_ENDPOINTS.EPLATA_CUSTOMER_API, API_ENDPOINTS.PATHS.CUSTOMER_PROFILE);
        responsePromise
            .then((response: CustomerMeResponse) => {
                this.userProfile.next(response);
            })
            .catch((error) => {
                console.error('customerProfile error:', error);
                this.userProfile.next(null);
                throw error;
            });
        return responsePromise;
    }

    // Fetch list of payees for a customer
    async fetchPayees(): Promise<Payee[]> {
        const responsePromise = this.apiCall('get', API_ENDPOINTS.EPLATA_CUSTOMER_API, API_ENDPOINTS.PATHS.PAYEE_LIST);
        responsePromise
            .then((response: { items: Payee[] }) => {
                this.payeesList.next(response.items || []);
            })
            .catch((error) => {
                console.error('fetchPayees error:', error);
                this.payeesList.next(null);
                throw error;
            });
        return responsePromise.then((response) => response.items || []);
    }

    // Delete Payee the current customer
    deletePayee(payload: { payeeId: string }): Promise<any> {
        const responsePromise = this.apiCall('del', API_ENDPOINTS.EPLATA_CUSTOMER_API, API_ENDPOINTS.PATHS.PAYEE_ARCHIVE, payload);
        responsePromise
            .then(() => {})
            .catch((error) => {
                console.error('deletePayee error:', error);
                throw error;
            });
        return responsePromise;
    }

    // Fetch account balance list for a customer
    async accountBalanceList(): Promise<any> {
        const responsePromise = this.apiCall('get', API_ENDPOINTS.EPLATA_CUSTOMER_API, API_ENDPOINTS.PATHS.ACCOUNT_BALANCE_LIST);
        responsePromise
            .then((response) => {
                this.balanceList.next(response);
            })
            .catch((error) => {
                console.error('fetchPayees error:', error);
                this.balanceList.next(null);
                throw error;
            });
        return responsePromise.then((response) => response || []);
    }

    // searchPayees method using promises
    async searchPayees(value: string, isInternal: boolean): Promise<Payee[]> {
        if (value.length < 2) {
            return [];
        }
        const isInternalParam = `isInternal=${isInternal}`;
        try {
            const response = await this.apiCall('get', API_ENDPOINTS.EPLATA_CUSTOMER_API, `${API_ENDPOINTS.PATHS.PAYEE_SEARCH}${encodeURIComponent(value)}&${isInternalParam}`);
            return response?.items || [];
        } catch (error) {
            console.error('Error searching payees:', error);
            return [];
        }
    }

    // Fetch customer transactionality list for a customer
    async customerTransactionality(): Promise<any> {
        const responsePromise = this.apiCall('get', API_ENDPOINTS.EPLATA_CUSTOMER_API, API_ENDPOINTS.PATHS.TRANSACTIONALITY);
        responsePromise
            .then(() => {})
            .catch((error) => {
                console.error('customer transactionality error:', error);
                throw error;
            });
        return responsePromise.then((response) => response || []);
    }

    // sendToken method using promises
    async sendToken(): Promise<any> {
        try {
            const response = await this.apiCall('post', API_ENDPOINTS.EPLATA_CUSTOMER_API, API_ENDPOINTS.PATHS.TOKEN_SEND, {});
            return response;
        } catch (error) {
            throw error;
        }
    }

    //verifyToken method using promises
    async verifyToken(token: string): Promise<any> {
        if (!token) {
            throw new Error('token is required for verifyToken');
        }
        try {
            const response = await this.apiCall('get', API_ENDPOINTS.EPLATA_CUSTOMER_API, `${API_ENDPOINTS.PATHS.VERIFY_TOKEN}?code=${token}`);
            return response;
        } catch (error) {
            console.error('Error verifying token:', error);
            throw error; // rethrow so the caller can handle it
        }
    }

    // createTransaction method using promises
    async createTransaction(data: any): Promise<any> {
        try {
            const response = await this.apiCall('post', API_ENDPOINTS.EPLATA_CUSTOMER_API, API_ENDPOINTS.PATHS.CREATE_TRANSACTION, data);
            return response;
        } catch (error) {
            throw error;
        }
    }
    // create Internal Transaction method using promises
    async createInternalTransaction(data: any): Promise<any> {
        try {
            const response = await this.apiCall('post', API_ENDPOINTS.EPLATA_CUSTOMER_API, API_ENDPOINTS.PATHS.CREATE_INTERNAL_TRANSACTION, data);
            return response;
        } catch (error) {
            throw error;
        }
    }

    // create Spei Transaction method using promises
    async createSpeiTransaction(data: any): Promise<any> {
        try {
            const response = await this.apiCall('post', API_ENDPOINTS.EPLATA_CUSTOMER_API, API_ENDPOINTS.PATHS.CREATE_SPEI_TRANSACTION, data);
            return response;
        } catch (error) {
            throw error;
        }
    }

    async getBankNameByClabe(clabe: string): Promise<string> {
        if (clabe.length !== 18) return 'Unsupported bank';
        try {
            const response = await this.apiCall('get', API_ENDPOINTS.CARD_USER_API_NAME, `${API_ENDPOINTS.PATHS.GET_BANK_NAME}${clabe}`);
            return response?.name || 'Unsupported bank';
        } catch (error) {
            console.error('Error fetching bank name:', error);
            return 'Unsupported bank';
        }
    }

    // Updates the session for a prospect user by sending the session ID
    async clearProspectSession(): Promise<any> {
        return this.apiCall('del', API_ENDPOINTS.EPLATA_PROSPECT_API, API_ENDPOINTS.PATHS.CLEAR_PROSPECT_SESSION);
    }

    // Updates the session for a customer user by sending the session ID
    async clearCustomerSession(): Promise<any> {
        return this.apiCall('del', API_ENDPOINTS.EPLATA_CUSTOMER_API, API_ENDPOINTS.PATHS.CLEAR_CUSTOMER_SESSION);
    }

    // findCustomerByClabe method using promises
    async findCustomerByClabe(clabe: string): Promise<any> {
        if (!clabe) {
            throw new Error('clabe is required for findCustomerByClabe');
        }
        try {
            const response = await this.apiCall('get', API_ENDPOINTS.EPLATA_CUSTOMER_API, `${API_ENDPOINTS.PATHS.FIND_CUSTOMER_BY_CLABE}${clabe}`);
            return response;
        } catch (error) {
            console.error('Error finding customer by clabe:', error);
            throw error;
        }
    }

    // Fetch list of bank for a customer
    async fetchListOfBank(): Promise<Bank[]> {
        const responsePromise = this.apiCall('get', API_ENDPOINTS.CARD_USER_API_NAME, API_ENDPOINTS.PATHS.LISTOFBANK);
        responsePromise
            .then((response: { items: Bank[] }) => {
                this.bankList.next(response.items || []);
            })
            .catch((error) => {
                console.error('fetchPayees error:', error);
                this.bankList.next(null);
                throw error;
            });
        return responsePromise.then((response) => response?.items || []);
    }

    // Fetch list of pay categories for a customer
    async getPayCategoriesList(params: GetPayCategoriesParams): Promise<PayCategory[]> {
        const path = `${API_ENDPOINTS.PATHS.PAY_CATEGORIES}?pageSize=${params.pageSize}&lastKey=${params.lastKey}&sortDirection=${params.sortDirection}`;
        const responsePromise = this.apiCall('get', API_ENDPOINTS.CARD_USER_API_NAME, path);
        responsePromise
            .then((response: PayCategory[]) => {
                this.categoriesList.next(response || []);
            })
            .catch((error) => {
                console.error('getPayCategoriesList error:', error);
                this.categoriesList.next(null);
                throw error;
            });
        return responsePromise.then((response) => response || []);
    }

    // Retrieves the current list of pay categories
    getCurrentCategories(): PayCategory[] | null {
        return this.categoriesList.value;
    }

    // Retrieves the current list of providers
    async getProviders(params: GetPayProvidersParams): Promise<PayProviders[]> {
        const path = `${API_ENDPOINTS.PATHS.PAY_PROVIDERS}?category=${params.category}`;
        const responsePromise = this.apiCall('get', API_ENDPOINTS.CARD_USER_API_NAME, path);
        responsePromise
            .then((response: PayProviders[]) => {
                this.providersList.next(response || []);
            })
            .catch((error) => {
                console.error('getProviders error:', error);
                this.providersList.next(null);
                throw error;
            });
        return responsePromise.then((response) => response || []);
    }

    // Retrieves the current list of providers
    async getListOfProviders(params: GetPayProvidersListParams): Promise<ProviderList[]> {
        const path = `${API_ENDPOINTS.PATHS.PAY_SERVICES}?sortDirection=${params.sortDirection}&service=${params.service}`;
        const responsePromise = this.apiCall('get', API_ENDPOINTS.CARD_USER_API_NAME, path);
        responsePromise
            .then((response: ProviderList[]) => {
                this.providersList.next(response || []);
            })
            .catch((error) => {
                console.error('getListOfProviders error:', error);
                this.providersList.next(null);
                throw error;
            });
        return responsePromise.then((response) => response || []);
    }

    // Method to verify a reference number for a payment
    async verificationReferenceNumber(payload: ValidateReferenceNumberPayload): Promise<any> {
        const responsePromise = this.apiCall('post', API_ENDPOINTS.CARD_USER_API_NAME, API_ENDPOINTS.PATHS.VERIFY_REFERENCE, payload);
        responsePromise.catch((error) => {
            console.error('verificationReferenceNumber error:', error);
            throw error;
        });
        return responsePromise;
    }

    // Fetch customer bulk list for a customer
    async fetchCustomerBulkList(): Promise<any[]> {
        try {
            const response = await this.apiCall(
                'get',
                API_ENDPOINTS.EPLATA_CUSTOMER_API,

                API_ENDPOINTS.PATHS.CUSTOMER_BULK_LIST
            );
            this.customerBulkList.next(response);
            return response;
        } catch (error) {
            console.error('fetchCustomerBulkList error:', error);
            this.customerBulkList.next(null);
            throw error;
        }
    }

    // Fetch customer bulk transactions details for a customer
    async fetchCustomerBulkTransactionsDetails(transactionGroupId: string | number): Promise<any[]> {
        try {
            const path = API_ENDPOINTS.PATHS.CUSTOMER_BULK_TRANSACTIONS_DETAILS.replace('{transactionGroupId}', String(transactionGroupId));
            const response = await this.apiCall('get', API_ENDPOINTS.EPLATA_CUSTOMER_API, path);
            this.customerBulkTransactionsList.next(response);
            return response;
        } catch (error) {
            console.error('fetchCustomerBulkTransactionsDetails error:', error);
            this.customerBulkTransactionsList.next(null);
            throw error;
        }
    }

    // Fetch customer bulk transactions pending for a customer
    async fetchCustomerBulkTransactionsPending(transactionGroupId: string | number): Promise<any[]> {
        try {
            const path = API_ENDPOINTS.PATHS.CUSTOMER_BULK_TRANSACTIONS_PENDING.replace('{transactionGroupId}', String(transactionGroupId));
            const response = await this.apiCall('get', API_ENDPOINTS.EPLATA_CUSTOMER_API, path);
            this.customerBulkTransactionsPending.next(response);
            return response;
        } catch (error) {
            console.error('fetchCustomerBulkTransactionsPending error:', error);
            this.customerBulkTransactionsPending.next(null);
            throw error;
        }
    }

    // Fetch customer bulk zip for a customer
    async fetchCustomerBulkZip(payload: { transactionGroupId: string }): Promise<any> {
        try {
            const response = await this.apiCall('post', API_ENDPOINTS.EPLATA_CUSTOMER_API, API_ENDPOINTS.PATHS.CUSTOMER_BULK_ZIP, payload);
            return response;
        } catch (error) {
            console.error('fetchCustomerBulkZip error:', error);
            throw error;
        }
    }

    // Fetch transaction group find for a customer
    async fetchTransactionGroupFind(id: string | number): Promise<any> {
        try {
            const path = API_ENDPOINTS.PATHS.CUSTOMER_TG_FIND.replace('{id}', String(id));
            const response = await this.apiCall('get', API_ENDPOINTS.EPLATA_CUSTOMER_API, path);
            this.customerTransactionalityFind.next(response);
            return response;
        } catch (error) {
            this.customerTransactionalityFind.next(null);
            console.error('fetchTransactionGroupFind error:', error);
            throw error;
        }
    }

    // create customer bulk for a customer
    async createCustomerBulk(data: any): Promise<any> {
        try {
            const response = await this.apiCall('post', API_ENDPOINTS.EPLATA_CUSTOMER_API, API_ENDPOINTS.PATHS.CUSTOMER_BULK_CREATE, data);
            return response;
        } catch (error) {
            console.error('createCustomerBulk error:', error);
            throw error;
        }
    }
    // Fetch transaction tracking details for a customer by transaction ID
    async fetchTransactionTracking(transactionId: string): Promise<any> {
        if (!transactionId) {
            throw new Error('transactionId is required for fetchTransactionTracking');
        }
        try {
            const path = `${API_ENDPOINTS.PATHS.CUSTOMER_TRACKING_TX}${encodeURIComponent(transactionId)}`;
            const response = await this.apiCall('get', API_ENDPOINTS.EPLATA_CUSTOMER_API, path);
            this.transactionTracking.next(response);
            return response;
        } catch (error) {
            console.error('fetchTransactionTracking error:', error);
            this.transactionTracking.next(null);
            throw error;
        }
    }

    // Download layout file
    async downloadLayout(): Promise<Blob> {
        try {
            const fileUrl = 'https://eplata-mx-website.s3.amazonaws.com/layout/LAYOUT_2024.xlsx';
            const response = await firstValueFrom(
                this.http.get(fileUrl, { responseType: 'blob' }).pipe(
                    catchError((error) => {
                        throw {
                            success: false,
                            statusCode: error?.status || 500,
                            message: error?.message || 'Error downloading layout'
                        };
                    })
                )
            );

            if (!response) {
                throw {
                    success: false,
                    statusCode: 500,
                    message: 'No response received from server'
                };
            }

            return response;
        } catch (error: any) {
            console.error('Error downloading layout:', error);
            await this.redirectIfSessionExpired(error?.statusCode);
            throw {
                success: false,
                statusCode: error.statusCode || 500,
                message: error.message || 'Error downloading layout'
            };
        }
    }

    // delete transaction group for a customer
    async deleteTransactionGroup(data: { id: string }): Promise<any> {
        try {
            const response = await this.apiCall('del', API_ENDPOINTS.EPLATA_CUSTOMER_API, API_ENDPOINTS.PATHS.TG_ARCHIVE, data);
            return response;
        } catch (error) {
            console.error('deleteTransactionGroup error:', error);
            throw error;
        }
    }

    // Fetch customer bulk item for a customer
    async fetchCustomerBulkItem(transactionGroupId: string | number): Promise<any> {
        try {
            const path = API_ENDPOINTS.PATHS.CUSTOMER_BULK_ITEM.replace('{transactionGroupId}', String(transactionGroupId));
            const response = await this.apiCall('get', API_ENDPOINTS.EPLATA_CUSTOMER_API, path);
            return response;
        } catch (error) {
            console.error('fetchCustomerBulkItem error:', error);
            throw error;
        }
    }

    // Process pending transaction group for a customer
    async processPendingTransactionGroup(data: any): Promise<any> {
        try {
            const response = await this.apiCall('post', API_ENDPOINTS.EPLATA_CUSTOMER_API, API_ENDPOINTS.PATHS.CUSTOMER_BULK_PROCESS, data);
            return response;
        } catch (error) {
            console.error('processPendingTransactionGroup error:', error);
            throw error;
        }
    }

    // Fetch customer bank statement by clabe and statement
    async fetchCustomerBankStatement(clabe: string, statement: string, token: number): Promise<any> {
        try {
            if (!clabe || !statement || !token) {
                throw new Error('clabe, statement, and token are required for fetchCustomerBankStatement');
            }
            const path = API_ENDPOINTS.PATHS.CUSTOMER_BANK_STATEMENT.replace('{clabe}', encodeURIComponent(clabe)).replace('{statement}', encodeURIComponent(statement)).replace('{token}', encodeURIComponent(token.toString()));
            const response = await this.apiCall('get', API_ENDPOINTS.CARD_USER_API_NAME, path);
            return response;
        } catch (error) {
            console.error('fetchCustomerBankStatement error:', error);
            throw error;
        }
    }

    // Add the method to fetch the transaction service list
    async fetchTransactionServiceList(params: { sortBy?: string; sortDir?: 'asc' | 'desc'; page?: number; nPerPage?: number }): Promise<TransactionServiceListResponse> {
        const { sortBy = 'createdAt', sortDir = 'desc', page = 0, nPerPage = 10 } = params;
        const path = `${API_ENDPOINTS.PATHS.TRANSACTION_SERVICE_LIST}?sortBy=${encodeURIComponent(sortBy)}&sortDir=${encodeURIComponent(sortDir)}&page=${page}&nPerPage=${nPerPage}`;
        // Call the API as usual
        const response: any = await this.apiCall('get', API_ENDPOINTS.EPLATA_CUSTOMER_API, path);
        const mappedResponse: TransactionServiceListResponse = {
            items: response.payments || [],
            total: response.count || 0,
            page,
            nPerPage
        };
        this.transactionServiceList.next(mappedResponse);
        return mappedResponse;
    }
    // Make a Gestopago payment
    async makeGestopagoPayment(paymentData: any): Promise<any> {
        if (!paymentData) {
            throw new Error('Payment data is required for makeGestopagoPayment');
        }
        try {
            const response = await this.apiCall('post', API_ENDPOINTS.CARD_USER_API_NAME, API_ENDPOINTS.PATHS.GESTOPAGO_PAYMENT, paymentData);

            return response;
        } catch (error) {
            console.error('makeGestopagoPayment error:', error);
            throw error;
        }
    }

    // Make transaction list with sorting and pagination
    async fetchTransactionList(params: { sortBy: string; sortDir: string; page: number; nPerPage: number; type?: string }): Promise<any> {
        try {
            let path = `${API_ENDPOINTS.PATHS.TRANSACTION_LIST}?sortBy=${encodeURIComponent(params.sortBy)}&sortDir=${encodeURIComponent(params.sortDir)}&page=${params.page}&nPerPage=${params.nPerPage}`;
            if (params.type) {
                path += `&type=${encodeURIComponent(params.type)}`;
            }
            const response = await this.apiCall('get', API_ENDPOINTS.EPLATA_CUSTOMER_API, path);
            return response;
        } catch (error) {
            console.error('fetchTransactionList error:', error);
            throw error;
        }
    }

    // fetch transaction report
    async fetchTransactionReport(reportData: any): Promise<any> {
        try {
            const response = await this.apiCall('post', API_ENDPOINTS.EPLATA_CUSTOMER_API, API_ENDPOINTS.PATHS.TRANSACTION_REPORT, reportData);
            return response;
        } catch (error) {
            console.error('fetchTransactionReport error:', error);
            throw error;
        }
    }

    // Search transactions with pagination and type filtering
    async searchTransactions(params: { nPerPage?: number; page?: number; type?: string }): Promise<any> {
        try {
            const queryParams = new URLSearchParams();
            if (params.nPerPage !== undefined) {
                queryParams.append('nPerPage', params.nPerPage.toString());
            }
            if (params.page !== undefined) {
                queryParams.append('page', params.page.toString());
            }
            if (params.type) {
                queryParams.append('type', params.type);
            }
            const path = `${API_ENDPOINTS.PATHS.SEARCH_TRANSACTIONS}?${queryParams.toString()}`;
            const response = await this.apiCall('get', API_ENDPOINTS.CARD_USER_API_NAME, path);
            return response;
        } catch (error) {
            console.error('searchTransactions error:', error);
            throw error;
        }
    }
    // Generate transaction receipt as a PDF Blob
    async generateTransactionReceipt(transactionId: string): Promise<Blob> {
        if (!transactionId || typeof transactionId !== 'string') {
            console.error('Invalid transactionId:', transactionId, 'type:', typeof transactionId);
            throw new Error('transactionId must be a non-empty string for generateTransactionReceipt');
        }

        try {
            const sanitizedTransactionId = String(transactionId).trim();
            if (!sanitizedTransactionId) {
                throw new Error('Sanitized transactionId is empty');
            }

            const apiName = String(API_ENDPOINTS.CARD_USER_API_NAME).trim();
            const path = String(API_ENDPOINTS.PATHS.TRANSACTION_PDF_RECEIPT).trim();
            if (!apiName || !path) {
                throw new Error('apiName or path is empty or invalid');
            }

            const init = {
                headers: {
                    Accept: 'application/json'
                },
                queryStringParameters: {
                    transactionId: sanitizedTransactionId
                }
            };

            const response = await this.apiCall('get', apiName, path, undefined, init);

            // Validate response
            if (!response?.success || !response?.pdf) {
                console.error('Invalid response:', response);
                throw new Error('Response does not contain valid PDF data');
            }

            // Convert base64 to Blob
            const byteCharacters = atob(response.pdf);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const pdfBlob = new Blob([byteArray], { type: 'application/pdf' });

            // Validate PDF content
            const arrayBuffer = await pdfBlob.arrayBuffer();
            const uint8Array = new Uint8Array(arrayBuffer.slice(0, 5));
            const textPreview = String.fromCharCode(...uint8Array);

            if (!textPreview.startsWith('%PDF-')) {
                console.error('Invalid PDF content, preview:', textPreview);
                throw new Error('Response is not a valid PDF');
            }

            return pdfBlob;
        } catch (error: any) {
            console.error('Error generating transaction receipt:', error);
            throw {
                success: false,
                statusCode: error?.statusCode || error?.response?.status || 500,
                message: error?.message || 'Error generating transaction receipt'
            };
        }
    }

    // Fetch general terms
    async fetchGeneralTerms(): Promise<any> {
        try {
            const response = await this.apiCall('get', API_ENDPOINTS.CARD_USER_API_NAME, API_ENDPOINTS.PATHS.GENERAL_TERMS);
            return response;
        } catch (error) {
            console.error('fetchGeneralTerms error:', error);
            throw error;
        }
    }

    // Fetch contract
    async fetchContract(): Promise<any> {
        try {
            const response = await this.apiCall('get', API_ENDPOINTS.CARD_USER_API_NAME, API_ENDPOINTS.PATHS.CONTRACT);
            return response;
        } catch (error) {
            console.error('fetchGeneralTerms error:', error);
            throw error;
        }
    }

    // Fetch agreement status
    async agreementStatus(): Promise<any> {
        try {
            const response = await this.apiCall('get', API_ENDPOINTS.CARD_USER_API_NAME, API_ENDPOINTS.PATHS.AGREEMENT_STATUS);
            this.agreementsOfContractAndTerms.next(response);
            return response;
        } catch (error) {
            this.agreementsOfContractAndTerms.next(null);
            console.error('fetchGeneralTerms error:', error);
            throw error;
        }
    }
}
